<template>
  <div class="file-list-item" :class="{ selected: showEdit, 'with-settings': showFileSettings }">
    <v2-pdf-sign
      v-if="item && showPdfSign"
      :showPdfSign="showPdfSign"
      :isReadyToSign="docReady"
      :file="item"
      :team="$route.params.team"
      :project="currentProject.uid"
      :getSelf="getProjectFile"
      :getFilePreview="getProjectFilePreview"
      :getSignatures="getFileSigningSignatures"
      :createSignature="createFileSigningSignature"
      :updateSignature="updateFileSigningSignature"
      :deleteSignature="deleteFileSigningSignature"
      :statiBits="statiBits"
      filetype="pdf"
      :locale="teams[$route.params.team].account.locale"
      @close="showPdfSign = false"
      @closeAndSetReady="closeAndSetReady"
      @closeAndSetFinished="closeAndSetFinished"
    ></v2-pdf-sign>
    <v2-delete-modal
      :name="'delete-quicklaunch-file-modal' + file.relationUid"
      :title="$t('quickLaunch.modals.deleteModal.title')"
      :text="$t('library.modals.deleteLibraryFile.text')"
      :startHtmlScrollToggle="!showFileDetails"
      :deleteHandler="
        () => {
          removeItem();
        }
      "
    ></v2-delete-modal>
    <div class="hidden md:flex flex-col w-full">
      <div class="flex w-full items-center">
        <v2-icon
          class="handle mr-6 cursor-pointer"
          color="#5057af"
          :data="require('@icon/Icon=menu.svg')"
          width="26px"
          height="26px"
        ></v2-icon>
        <div
          v-if="item.display_type === 'label'"
          style="width: 50px; height: 50px"
          class="flex justify-center items-center"
        >
          <v2-icon :data="require('@icon/Icon=type.svg')" width="40px" height="40px"></v2-icon>
        </div>
        <div style="width: 50px; height: 100%" v-if="item.display_type !== 'label'">
          <div v-if="!isReady" style="width: 50px; height: 50px" class="relative">
            <v-spinner slot="value" size="10" :loading="true"></v-spinner>
          </div>
          <div v-if="isConverted" style="width: 50px; height: 50px" class="flex justify-center">
            <v-lazy-load :image="imageUrl"></v-lazy-load>
          </div>
        </div>
        <div class="flex flex-2 items-center ml-2">
          <div v-if="!showEdit" class="pl-13px">
            <div class="line-clamp-1 break-all">
              {{ item.title }}
              <v2-icon
                :data="require('@icon/i-check-mark-icon.svg')"
                width="18"
                height="18"
                class="file-signing-status-icon"
                v-if="hasSigning"
                :class="[
                  (docReady && !signingFinished) || configFinishedClientOnly
                    ? 'configured'
                    : signingFinished
                    ? 'finished'
                    : 'new',
                ]"
              ></v2-icon>
            </div>
            <div class="text-v2-grey-light text-xs" v-if="item.display_type !== 'label'">
              <span>{{ $h.formatDate(file.created_at, teams[$route.params.team].account.timezone) + " | " }}</span>
              <span class="uppercase">{{ file.file_extension + " | " }}</span>
              <span>{{ $h.formatFileSize(file.file_size) }}</span>
              <span v-if="!isConverted">{{ " | " + $t("stati.files.text." + currentStatus) }}</span>
            </div>
          </div>
          <v2-textfield
            v-if="showEdit"
            v-model="title"
            name="title"
            :class="['_' + item.uid]"
            class="flex"
          ></v2-textfield>
        </div>
        <div class="flex items-center">
          <v2-button
            v-show="!showEdit"
            :title="$t('quickLaunch.actions.editTitle')"
            class="ml-2"
            ghostProgress
            icon
            sm
            @click.native="showEditField"
          >
            <v2-icon :data="require('@icon/Icon=edit-2.svg')" width="20px" height="20px" color="inherit"></v2-icon>
          </v2-button>
          <v2-button
            v-show="showEdit"
            :title="$t('quickLaunch.actions.saveTitle')"
            class="ml-2"
            ghostProgress
            icon
            sm
            @click.native="saveTitle"
          >
            <v2-icon :data="require('@icon/Icon=check.svg')"></v2-icon>
          </v2-button>
          <v2-button
            :title="$t('quickLaunch.actions.deleteFile')"
            ghostProgress
            icon
            iconWarning
            sm
            class="ml-2"
            @click.native="openDeleteFileModal"
          >
            <v2-icon :data="require('@icon/Icon=trash.svg')" width="20px" height="20px" color="inherit"></v2-icon>
          </v2-button>
          <v2-button
            v-if="isReady && isSignablePdf"
            :title="$t('fileSigning.actions.fileSettings')"
            ghostProgress
            icon
            sm
            class="ml-2"
            @click.native="toggleFileSettings"
          >
            <v2-icon :data="require('@icon/Icon=settings.svg')" width="20px" height="20px"></v2-icon>
          </v2-button>
          <v2-button
            v-if="signingFinished"
            :title="$t('fileSigning.actions.fileDownload')"
            ghostProgress
            icon
            sm
            class="ml-2"
            @click.native="downloadFile"
          >
            <v2-icon :data="require('@icon/Icon=download.svg')" width="20px" height="20px"></v2-icon>
          </v2-button>
          <v2-button
            v-if="signingFinished"
            :title="$t('fileSigning.actions.fileSigningSummary')"
            ghostProgress
            icon
            sm
            class="ml-2"
            @click.native="downloadFileSigningSummary"
          >
            <v2-icon
              :data="require('@icon/fa-file-lines.svg')"
              width="20px"
              height="20px"
              class="fa-file-lines"
            ></v2-icon>
          </v2-button>
        </div>
      </div>
      <div
        v-if="showFileSettings && teams[$route.params.team].account.team.attributes.file_signing_enabled"
        class="file-settings flex flex-wrap"
      >
        <div class="flex items-center">
          <div>{{ $t("fileSigning.labels.activateFileSigning") }}</div>
          <v2-switch
            small
            v-model="fileSigningActive"
            @input="toggleActivateFileSigning"
            :disabled="signingFinished"
            :name="'addFileSigning_' + item.uid"
          ></v2-switch>
        </div>
        <v2-button
          v-if="fileSigningActive && !docReady"
          primary
          @click.native="openFileViewer"
          :title="$t('fileSigning.actions.docConfig')"
        >
          {{ $t("fileSigning.actions.docConfig") }}
        </v2-button>
        <v2-button
          v-if="fileSigningActive && configFinished"
          primary
          @click.native="openFileViewer"
          :title="$t('fileSigning.actions.signDocMember')"
        >
          {{ $t("fileSigning.actions.signDocMember") }}
        </v2-button>
        <div v-if="fileSigningActive && configFinished" class="w-full flex items-center mt-2 text-v2-grey-light">
          {{ $t("fileSigning.texts.configFinished") }}
        </div>
        <div
          v-if="fileSigningActive && configFinishedClientOnly"
          class="w-full flex items-center mt-2 text-v2-grey-light"
        >
          {{ $t("fileSigning.texts.configFinishedClientOnly") }}
        </div>
        <div
          v-if="fileSigningActive && signingFinished"
          class="w-full w-full flex items-center mt-2 text-v2-grey-light"
        >
          {{ $t("fileSigning.texts.signingFinished") }}
        </div>
        <div v-if="fileSigningActive && userSigningFinished" class="w-full flex items-center mt-2 text-v2-grey-light">
          {{ $t("fileSigning.texts.userSigningFinished") }}
        </div>
        <div v-if="fileSigningActive && !isPwProtected" class="w-full mt-8">
          <div class="w-1/2" v-html="$t('fileSigning.texts.notPwProtected')"></div>
          <div class="w-full mt-4">
            <v2-button
              primary
              @click.native="enablePwProtection"
              :title="$t('fileSigning.actions.enablePwProtection')"
              >{{ $t("fileSigning.actions.enablePwProtection") }}</v2-button
            >
          </div>
        </div>
      </div>
    </div>
    <div class="flex items-center md:hidden w-full">
      <div>
        <v2-icon
          class="handle mr-4 sm:mr-6 cursor-pointer"
          :data="require('@icon/Icon=menu.svg')"
          color="#5057af"
          width="26px"
          height="26px"
        ></v2-icon>
      </div>
      <div class="flex-2 pr-4">
        <div>
          <div class="line-clamp-1 break-all">{{ item.title }}</div>
          <div class="text-v2-grey-light text-xs" v-if="item.display_type !== 'label'">
            <span>{{ $h.formatDate(file.created_at, teams[$route.params.team].account.timezone) + " | " }}</span>
            <span class="uppercase">{{ file.file_extension + " | " }}</span>
            <span>{{ $h.formatFileSize(file.file_size) }}</span>
            <span v-if="!isConverted">{{ " | " + $t("stati.files.text." + currentStatus) }}</span>
          </div>
        </div>
      </div>
      <div>
        <v2-button ghost sm icon @click.native="toggleFileDetails">
          <v2-icon :data="require('@icon/Icon=more-horizontal.svg')" width="22px" color="inherit"></v2-icon>
        </v2-button>
      </div>
      <div class="overlay" v-show="showFileDetails"></div>
      <div class="file-details fixed top-20" v-if="showFileDetails">
        <div class="details">
          <div @click="toggleFileDetails">
            <v2-icon
              :data="require('@icon/Icon=x.svg')"
              width="24px"
              height="24px"
              class="absolute right-3 top-2 cursor-pointer"
            ></v2-icon>
          </div>
          <div class="pl-13px py-11px" v-if="!showEdit">{{ item.title }}</div>
          <div class="flex items-center" v-if="showEdit">
            <v2-textfield v-model="title" name="title" :class="['_' + item.uid]" class="flex flex-1"></v2-textfield>
            <v2-button class="ml-2" primary icon sm @click.native="saveTitle">
              <v2-icon :data="require('@icon/Icon=check.svg')"></v2-icon>
            </v2-button>
          </div>
          <div class="flex mt-4" v-if="item.display_type !== 'label'">
            <div style="width: 100px" class="mr-8 pl-13px">
              <div v-if="!isReady" style="width: 50px; height: 100%" class="relative">
                <v-spinner slot="value" size="10" :loading="true"></v-spinner>
              </div>
              <div v-if="isConverted" style="width: 100px; height: 100px" class="flex justify-center">
                <v-lazy-load :image="imageUrl"></v-lazy-load>
              </div>
            </div>
            <div class="flex flex-col justify-center text-v2-grey-light text-xs leading-5">
              <div>
                {{ $h.formatDate(file.created_at, teams[$route.params.team].account.timezone) }}
              </div>
              <div class="uppercase">{{ file.file_extension }}</div>
              <div>{{ $h.formatFileSize(file.file_size) }}</div>
              <div v-if="!isConverted">
                {{ $t("stati.files.text." + currentStatus) }}
              </div>
            </div>
          </div>
        </div>
        <div class="buttons">
          <v2-button outline greyDark whiteBg fullWidth @click.native="showEditField"
            >{{ $t("actions.edit") }}
          </v2-button>
          <v2-button outline danger whiteBg fullWidth class="mt-1" @click.native="openDeleteFileModal"
            >{{ $t("actions.delete") }}
          </v2-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

export default {
  name: "QuickLaunchFileListItem",
  props: {
    file: Object,
    component: Object,
  },
  data() {
    return {
      title: "",
      showEdit: false,
      item: {},
      amountTry: 15,
      timeWaitStart: 3000,
      timer: null,
      imageUrl: "",
      showFileDetails: false,
      showFileSettings: false,
      showPdfSign: false,
      currentDeleteFileUid: null,
      fileSigningActive: false,
      fileSigning: null,
      fileSigningSignatures: null,
    };
  },
  created() {
    this.item = JSON.parse(JSON.stringify(this.file));
    this.title = this.file.title;
    if (this.item.display_type !== "label") this.untilReady();
    if (this.isSignablePdf && this.hasSigning) {
      const vm = this;
      vm.getFileSigning({ params: { file: vm.item.uid, project: vm.currentProject.uid } })
        .then((res) => {
          vm.$set(vm, "fileSigning", res);
          vm.fileSigningActive = true;
          if (vm.fileSigning.ready_at) {
            vm.getFileSigningSignatures({ params: { project: vm.currentProject.uid, file: vm.item.uid } })
              .then((res) => {
                vm.$set(vm, "fileSigningSignatures", res);
              })
              .catch((err) => {
                console.log("ERR", err);
              });
          }
        })
        .catch((err) => {
          console.log("err", err);
          this.fileSigningActive = false;
        });
    }
  },
  destroyed() {
    if (this.timer) {
      clearTimeout(this.timer);
    }
  },
  methods: {
    ...mapActions([
      "getProjectFile",
      "getProjectFileImagePreview",
      "getTemplateFilePreview",
      "getLibraryImagePreview",
      "getProjectComponent",
      "updateComponentRelation",
      "deleteComponentRelation",
      "getProjectFilePreview",
      "getFileSigning",
      "createFileSigning",
      "updateFileSigning",
      "getFileSigningSignatures",
      "createFileSigningSignature",
      "updateFileSigningSignature",
      "deleteFileSigning",
      "deleteFileSigningSignature",
      "editProject",
      "downloadProjectFile",
      "downloadProjectFileSigningSummary",
    ]),
    toggleFileDetails() {
      this.$h.toggleHtmlScroll();
      this.showFileDetails = !this.showFileDetails;
    },
    toggleFileSettings() {
      this.showFileSettings = !this.showFileSettings;
    },
    toggleActivateFileSigning() {
      const vm = this;
      if (this.fileSigningActive) {
        vm.createFileSigning({ customLoading: true, params: { file: vm.item.uid, project: vm.currentProject.uid } })
          .then((res) => {
            vm.$set(vm, "fileSigning", res);
            this.item.has_signing = true;
            vm.$snotify.success(vm.$t("fileSigning.toasts.createFileSigning.success"), vm.$t("toasts.success"), {
              icon: vm.$successIcon,
            });
          })
          .catch(() => {
            vm.$snotify.error(vm.$t("fileSigning.toasts.createFileSigning.error"), vm.$t("toasts.error"), {
              timeout: 0,
              icon: vm.$errorIcon,
            });
          });
      } else {
        vm.deleteFileSigning({ customLoading: true, params: { file: vm.item.uid, project: vm.currentProject.uid } })
          .then(() => {
            vm.$set(vm, "fileSigning", null);
            this.item.has_signing = false;
            vm.$snotify.success(vm.$t("fileSigning.toasts.deleteFileSigning.success"), vm.$t("toasts.success"), {
              icon: vm.$successIcon,
            });
          })
          .catch(() => {
            vm.$snotify.error(vm.$t("fileSigning.toasts.deleteFileSigning.error"), vm.$t("toasts.error"), {
              timeout: 0,
              icon: vm.$errorIcon,
            });
          });
      }
    },
    openFileViewer() {
      this.showPdfSign = true;
    },
    showEditField() {
      this.showEdit = true;
      this.$nextTick(() => {
        const str = "._" + this.file.uid + " .textfield";
        const el = document.querySelectorAll(str)[0];
        el.focus();
      });
    },
    saveTitle() {
      const vm = this;
      const data = {
        customLoading: true,
        params: {
          project: this.currentProject.uid,
          component: this.component.uid,
          relation: this.file.relationUid,
        },
        data: {
          title: this.title,
        },
      };
      vm.updateComponentRelation(data)
        .then(() => {
          this.item.title = this.title;
          this.$emit("reload");
          this.showEdit = false;
          vm.$snotify.success(vm.$t("quickLaunch.toasts.saveTitle.success"), vm.$t("toasts.success"), {
            icon: vm.$successIcon,
          });
        })
        .catch(() => {
          this.showEdit = false;
          vm.$snotify.error(vm.$t("quickLaunch.toasts.saveTitle.error"), vm.$t("toasts.error"), {
            timeout: 0,
            icon: vm.$errorIcon,
          });
        });
    },
    openDeleteFileModal() {
      this.$modal.show("delete-quicklaunch-file-modal" + this.file.relationUid);
    },
    removeItem() {
      const vm = this;
      const params = {
        project: vm.currentProject.uid,
        component: vm.component.uid,
        relation: this.file.relationUid,
      };
      vm.deleteComponentRelation({ customLoading: true, params })
        .then(() => {
          this.$modal.hide("delete-quicklaunch-file-modal" + this.file.relationUid);
          this.$emit("reload");
          vm.$snotify.success(vm.$t("quickLaunch.toasts.deleteItem.success"), vm.$t("toasts.success"), {
            icon: vm.$successIcon,
          });
        })
        .catch(() => {
          vm.$snotify.error(vm.$t("quickLaunch.toasts.deleteItem.error"), vm.$t("toasts.error"), {
            timeout: 0,
            icon: vm.$errorIcon,
          });
        });
    },
    isConvertedCalculate(item) {
      return this.statiBits ? this.$h.globalIsBitSet(item.status, this.statiBits.CONVERTED.bit) : false;
    },
    isReadyCalculate(item) {
      if (this.statiBits && item.status) {
        return (
          this.$h.globalCheckFileStatus(item.status, this.statiBits) !== "VIRUS_SCANNING" &&
          this.$h.globalCheckFileStatus(item.status, this.statiBits) !== "CONVERTING"
        );
      } else {
        return false;
      }
    },
    getItemPreview() {
      const data = {
        team: this.$route.params.team,
        project: this.currentProject.uid,
        template: this.currentProject.template.uid,
        file: this.item.uid,
        customLoading: true,
        folder: this.item.folder,
      };
      if (this.item.library_type === "template") {
        if (this.isConverted && this.item.preview.image) {
          this.getTemplateFilePreview(data).then((res) => {
            this.imageUrl = res;
          });
        } else {
          this.imageUrl = "";
        }
      } else if (this.item.library_type === "library") {
        if (this.isConverted && this.item.preview.image) {
          this.getLibraryImagePreview(data).then((res) => {
            this.imageUrl = res;
          });
        } else {
          this.imageUrl = "";
        }
      } else {
        if (this.isConverted && this.item.preview.image) {
          this.getProjectFileImagePreview(data).then((res) => {
            this.imageUrl = res;
          });
        } else {
          this.imageUrl = "";
        }
      }
    },
    untilReady() {
      this.getItemPreview();
      const data = {
        file: this.file.uid,
        project: this.currentProject.uid,
        customLoading: true,
      };
      if (!this.isReady) {
        const vm = this;
        vm.timer = setTimeout(function () {
          vm.getProjectFile(data)
            .then((response) => {
              vm.item = response;
              vm.amountTry = vm.amountTry - 1;
              if (vm.amountTry <= 0) {
                vm.amountTry = 10;
                vm.timeWaitStart = vm.timeWaitStart + 60000;
              }
              vm.untilReady();
            })
            .catch();
        }, vm.timeWaitStart);
      }
    },
    closeAndSetReady() {
      const vm = this;
      this.showPdfSign = false;
      const payload = {
        params: {
          project: this.currentProject.uid,
          file: this.item.uid,
        },
        data: {
          ready: true,
        },
      };
      this.updateFileSigning(payload)
        .then((res) => {
          vm.$set(vm, "fileSigning", res);
          if (vm.fileSigning.ready_at) {
            vm.getFileSigningSignatures({ params: { project: vm.currentProject.uid, file: vm.item.uid } })
              .then((res) => {
                vm.$set(vm, "fileSigningSignatures", res);
              })
              .catch((err) => {
                console.log("ERROR", err);
              });
          }
          vm.$snotify.success(vm.$t("fileSigning.toasts.finishFileSigningConfig.success"), vm.$t("toasts.success"), {
            icon: vm.$successIcon,
          });
        })
        .catch(() => {
          vm.$snotify.error(vm.$t("fileSigning.toasts.finishFileSigningConfig.error"), vm.$t("toasts.error"), {
            timeout: 0,
            icon: vm.$errorIcon,
          });
        });
    },
    closeAndSetFinished() {
      const vm = this;
      this.showPdfSign = false;
      const payload = {
        params: {
          project: this.currentProject.uid,
          file: this.item.uid,
        },
        data: {
          user_finished: true,
        },
      };
      this.updateFileSigning(payload)
        .then((res) => {
          vm.$set(this, "fileSigning", res);
          vm.$snotify.success(
            vm.$t("fileSigning.toasts.finishFileSigningUserSignatures.success"),
            vm.$t("toasts.success"),
            {
              icon: vm.$successIcon,
            }
          );
        })
        .catch(() => {
          vm.$snotify.error(vm.$t("fileSigning.toasts.finishFileSigningUserSignatures.error"), vm.$t("toasts.error"), {
            timeout: 0,
            icon: vm.$errorIcon,
          });
        });
    },
    downloadFile() {
      this.downloadProjectFile({
        file: this.item.uid,
        project: this.currentProject.uid,
        redirect: false,
      }).then((res) => {
        window.location = res.data.data.redirect;
      });
    },
    downloadFileSigningSummary() {
      this.downloadProjectFileSigningSummary({
        file: this.item.uid,
        project: this.currentProject.uid,
        redirect: false,
      });
    },
    enablePwProtection() {
      const vm = this;
      let data = {
        private: true,
      };
      vm.editProject({
        customLoading: true,
        params: { project: this.currentProject.uid },
        data,
      })
        .then(() => {
          vm.$snotify.success(vm.$t("fileSigning.toasts.enablePwProtection.success"), vm.$t("toasts.success"), {
            icon: vm.$successIcon,
          });
          vm.getProject({
            customLoading: false,
            project: vm.currentProject.uid,
          })
            .then(() => {
              vm.initProjectData();
            })
            .catch(() => {
              vm.isCustomLoading = false;
            });
        })
        .catch((err) => {
          if (err.response && !err.response.data.error.custom) {
            vm.$snotify.error(vm.$t("fileSigning.toasts.enablePwProtection.error"), vm.$t("toasts.error"), {
              timeout: 0,
              icon: vm.$errorIcon,
            });
          }
        });
    },
  },
  computed: {
    isPwProtected() {
      return this.currentProject.private;
    },
    isSignablePdf() {
      return (
        this.item.display_type === "pdf" &&
        this.item.library_type === "project" &&
        this.teams[this.$route.params.team].account.team.attributes.file_signing_enabled
      );
    },
    isConverted() {
      return this.isConvertedCalculate(this.item);
    },
    isReady() {
      return this.isReadyCalculate(this.item);
    },
    currentStatus() {
      return this.statiBits && this.item ? this.$h.globalGetCurrentFileStatus(this.item.status, this.statiBits) : "";
    },
    hasSigning() {
      return this.item && this.item.has_signing ? true : false;
    },
    docReady() {
      return this.fileSigning && this.fileSigning.ready_at ? true : false;
    },
    docFinished() {
      return this.fileSigning && this.fileSigning.user_finished_at ? true : false;
    },
    configFinished() {
      return this.fileSigning && this.fileSigning.ready_at && !this.fileSigning.user_finished_at ? true : false;
    },
    clientOnly() {
      if (this.fileSigningSignatures) {
        return (
          this.fileSigningSignatures.some((el) => el.signer_type === "client") &&
          !this.fileSigningSignatures.some((el) => el.signer_type === "user")
        );
      } else {
        return false;
      }
    },
    configFinishedClientOnly() {
      return this.fileSigning &&
        this.fileSigning.ready_at &&
        this.fileSigning.user_finished_at &&
        this.clientOnly &&
        !this.signingFinished
        ? true
        : false;
    },
    signingFinished() {
      return this.fileSigning && this.fileSigning.ready_at && this.fileSigning.finished_at ? true : false;
    },
    userSigningFinished() {
      return this.fileSigning &&
        this.fileSigning.ready_at &&
        this.fileSigning.user_finished_at &&
        !this.clientOnly &&
        !this.signingFinished
        ? true
        : false;
    },
    ...mapGetters({
      teams: "auth_get_teams",
      statiBits: "global_get_fileStatiBits",
      currentProject: "projects_get_current_project",
    }),
  },
  watch: {
    file: function () {
      this.item = JSON.parse(JSON.stringify(this.file));
    },
  },
};
</script>

<style lang="scss">
#spinner {
  z-index: 9994;
}
.file-list-item {
  @apply mb-2 flex items-center bg-white px-5 rounded-lg border-2 border-white py-4;
  &.selected,
  &.ghost {
    @apply border-v2-cta-blue;
  }

  &.with-settings {
    height: auto;
  }
}

.overlay {
  @apply bg-v2-black opacity-75 w-full h-full fixed;
  z-index: 10005;
  top: 0;
  left: 0;
}

.file-details {
  @apply flex flex-col absolute w-full;
  left: 0;
  z-index: 10010;

  .details {
    @apply bg-white rounded-xl px-4 py-10 mx-1;
  }

  .buttons {
    @apply px-1 mt-2.5;
  }
}

.file-settings {
  min-height: 65px;
  @apply border-t border-v2-cta-blue w-full flex justify-between mt-4 pt-4;
}

.file-signing-status-icon {
  @apply mb-0.5;
  &.finished {
    fill: rgba(19, 196, 106, 1);
  }

  &.configured {
    fill: rgba(246, 205, 121, 1);
  }

  &.new {
    fill: black;
  }
}
</style>
